import { Injectable, inject } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import {
    InteractionType,
    BrowserConfigurationAuthError,
    BrowserUtils,
    PopupRequest,
    RedirectRequest,
    AuthenticationResult
} from '@azure/msal-browser';
import { Observable, of } from 'rxjs';
import { concatMap, catchError, map } from 'rxjs/operators';

import { UserRole } from '../enums/UserRole.enum';
import { hashContainsKnownMsalProperties } from '../utils/msal-hash.util';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';

@Injectable()
export class MsalRoleGuard {
    private loginFailedRoute?: UrlTree;
    private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
    private msalBroadcastService = inject(MsalBroadcastService);
    private authService = inject(MsalService);
    private location = inject(Location);
    private router = inject(Router);

    constructor() {
        this.msalBroadcastService.inProgress$.subscribe();
    }

    parseUrl(url: string): UrlTree {
        return this.router.parseUrl(url);
    }

    getDestinationUrl(path: string): string {
        const baseElements = document.getElementsByTagName('base');
        const baseUrl = this.location.normalize(baseElements.length ? baseElements[0].href : window.location.origin);
        const pathUrl = this.location.prepareExternalUrl(path);
        return pathUrl.startsWith('#') ? `${baseUrl}/${pathUrl}` : `${baseUrl}${pathUrl}`;
    }

    private loginInteractively(state: RouterStateSnapshot): Observable<boolean> {
        const authRequest =
            typeof this.msalGuardConfig.authRequest === 'function'
                ? this.msalGuardConfig.authRequest(this.authService, state)
                : { ...this.msalGuardConfig.authRequest };

        if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
            return this.authService.loginPopup(authRequest as PopupRequest).pipe(
                map((response: AuthenticationResult) => {
                    this.authService.instance.setActiveAccount(response.account);
                    return true;
                })
            );
        }

        const redirectStartPage = this.getDestinationUrl(state.url);
        return this.authService
            .loginRedirect({
                redirectStartPage,
                ...authRequest
            } as RedirectRequest)
            .pipe(map(() => false));
    }

    private activateHelper(route?: ActivatedRouteSnapshot, state?: RouterStateSnapshot): Observable<boolean | UrlTree> {
        if (
            this.msalGuardConfig.interactionType !== InteractionType.Popup &&
            this.msalGuardConfig.interactionType !== InteractionType.Redirect
        ) {
            throw new BrowserConfigurationAuthError(
                'invalid_interaction_type',
                'Invalid interaction type provided to MSAL Guard.'
            );
        }

        if (typeof window !== 'undefined') {
            if (
                hashContainsKnownMsalProperties(window.location.hash) &&
                BrowserUtils.isInIframe() &&
                !this.authService.instance.getConfiguration().system.allowRedirectInIframe
            ) {
                return of(false);
            }
        } else {
            return of(true);
        }

        if (this.msalGuardConfig.loginFailedRoute) {
            this.loginFailedRoute = this.parseUrl(this.msalGuardConfig.loginFailedRoute);
        }

        const currentPath = this.location.path(true);

        return this.authService.initialize().pipe(
            concatMap(() => this.authService.handleRedirectObservable()),
            concatMap(() => {
                const accounts = this.authService.instance.getAllAccounts();
                if (!accounts.length) {
                    return state ? this.loginInteractively(state) : of(false);
                }

                if (state && currentPath.includes('#code')) {
                    return of(this.parseUrl(this.location.path()));
                }

                // Role-based validation
                const claims = this.authService.instance.getActiveAccount()?.idTokenClaims;
                const roles = claims?.roles as UserRole[] | undefined;

                if (!roles || roles.length === 0) {
                    return of(this.router.parseUrl('/unauthorized'));
                }

                if (route?.data['roles']?.length) {
                    const allowedRoles = route.data['roles'] as UserRole[];
                    const hasRole = allowedRoles.some(role => roles.includes(role));
                    return of(hasRole ? true : this.router.parseUrl('/unauthorized'));
                }

                return of(true);
            }),
            catchError(() => {
                if (this.loginFailedRoute && state) {
                    return of(this.loginFailedRoute);
                }
                return of(false);
            })
        );
    }

    includesCode(path: string): boolean {
        return path.endsWith('/code') || path.includes('#code=') || path.includes('&code=');
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
        return this.activateHelper(route, state);
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
        return this.activateHelper(route, state);
    }

    canMatch(): Observable<boolean | UrlTree> {
        return this.activateHelper();
    }
}
